import { getLineAmountMinor } from "../pricing/calculate.ts";
import type { ItemStatus } from "../pricing/types.ts";

type Booleanish = boolean | 0 | 1;

export type EditableQuoteItem = {
  id: string;
  nameEn: string;
  nameZh: string | null;
  unit: string;
  days: number | null;
  quantity: number;
  rateMinor: number;
  amountMinor: number;
  status: ItemStatus;
  taxable: Booleanish;
  visibleToClient: Booleanish;
  includeInTotal: Booleanish;
  customValues?: Record<string, string>;
};

export type EditableQuoteItemPatch = Partial<Omit<EditableQuoteItem, "id">>;

export type BulkItemsPayload =
  | {
      ok: true;
      data: {
        items: Array<EditableQuoteItem & { taxable: boolean; visibleToClient: boolean; includeInTotal: boolean }>;
      };
    }
  | {
      ok: false;
      error: string;
    };

export function updateEditableQuoteItem(items: EditableQuoteItem[], itemId: string, patch: EditableQuoteItemPatch): EditableQuoteItem[] {
  return items.map((item) => {
    if (item.id !== itemId) {
      return item;
    }

    const updated = {
      ...item,
      ...patch,
    };

    if (shouldRecalculateAmount(patch)) {
      return {
        ...updated,
        amountMinor: getLineAmountMinor({
          ...updated,
          amountMinor: null,
          taxable: toBoolean(updated.taxable),
          visibleToClient: toBoolean(updated.visibleToClient),
          includeInTotal: toBoolean(updated.includeInTotal),
        }),
      };
    }

    return updated;
  });
}

export function buildBulkItemsPayload(items: EditableQuoteItem[]): BulkItemsPayload {
  const normalizedItems = items.map((item) => ({
    id: item.id,
    nameEn: item.nameEn.trim(),
    nameZh: item.nameZh?.trim() || null,
    unit: item.unit.trim(),
    days: item.days,
    quantity: item.quantity,
    rateMinor: Math.round(item.rateMinor),
    amountMinor: Math.round(item.amountMinor),
    status: item.status,
    taxable: toBoolean(item.taxable),
    visibleToClient: toBoolean(item.visibleToClient),
    includeInTotal: toBoolean(item.includeInTotal),
    customValues: Object.fromEntries(
      Object.entries(item.customValues ?? {}).map(([key, value]) => [key.trim(), value.trim()]).filter(([key]) => key.length > 0),
    ),
  }));

  if (normalizedItems.some((item) => !item.nameEn)) {
    return {
      ok: false,
      error: "Each quotation item needs an English item name.",
    };
  }

  if (normalizedItems.some((item) => !item.unit)) {
    return {
      ok: false,
      error: "Each quotation item needs a unit.",
    };
  }

  return {
    ok: true,
    data: {
      items: normalizedItems,
    },
  };
}

function shouldRecalculateAmount(patch: EditableQuoteItemPatch): boolean {
  return !("amountMinor" in patch) && ("quantity" in patch || "days" in patch || "rateMinor" in patch);
}

function toBoolean(value: Booleanish): boolean {
  return value === true || value === 1;
}
